// What a support request is about, carried from the page where the learner
// got stuck to the contact form (report 10, FLOW-09). Only short opaque
// references travel in the URL: never tokens, signed asset URLs or gateway
// payloads.

export const SUPPORT_PATH = "/contact-us";

export const SUPPORT_TOPICS = Object.freeze({
  lesson: "lesson",
  certificate: "certificate",
  payment: "payment",
  batchRequest: "batch_request",
});

const TOPIC_VALUES = new Set(Object.values(SUPPORT_TOPICS));
const REFERENCE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const cleanReference = (value) => {
  const text = String(value ?? "").trim();
  return REFERENCE_PATTERN.test(text) ? text : null;
};

export const buildSupportHref = ({
  topic,
  reference,
  courseId,
  lessonId,
} = {}) => {
  const params = new URLSearchParams();
  if (TOPIC_VALUES.has(topic)) params.set("topic", topic);
  const ref = cleanReference(reference);
  if (ref) params.set("ref", ref);
  const course = cleanReference(courseId);
  if (course) params.set("course", course);
  const lesson = cleanReference(lessonId);
  if (lesson) params.set("lesson", lesson);
  const query = params.toString();
  return query ? `${SUPPORT_PATH}?${query}` : SUPPORT_PATH;
};

// Anything that does not match the expected shape is dropped, so a crafted
// link cannot inject text into the form.
export const readSupportContext = (search) => {
  const params = new URLSearchParams(search || "");
  const topic = params.get("topic");
  return {
    topic: TOPIC_VALUES.has(topic) ? topic : null,
    reference: cleanReference(params.get("ref")),
    courseId: cleanReference(params.get("course")),
    lessonId: cleanReference(params.get("lesson")),
  };
};

const TOPIC_SUBJECTS = Object.freeze({
  [SUPPORT_TOPICS.lesson]: "Course content isn't working",
  [SUPPORT_TOPICS.certificate]: "Certificate not received",
  [SUPPORT_TOPICS.payment]: "Help with a payment",
  [SUPPORT_TOPICS.batchRequest]: "Help with a live training request",
});

// The contact form's prefill for a context. It uses only fixed wording and
// the references themselves (re-checked here) and looks nothing up, so a link
// can neither inject text nor reveal anyone's data. Returns null when there
// is no context.
export const describeSupportContext = ({
  topic,
  reference,
  courseId,
  lessonId,
} = {}) => {
  const ref = cleanReference(reference);
  const course = cleanReference(courseId);
  const lesson = cleanReference(lessonId);
  const label = TOPIC_SUBJECTS[TOPIC_VALUES.has(topic) ? topic : ""];
  if (!label && !ref && !course && !lesson) return null;
  const subject = label || "Support request";
  const primary = ref || lesson || course;
  const summary = [
    subject,
    ref && `Ref ${ref}`,
    course && `Course ID ${course}`,
    lesson && `Lesson ID ${lesson}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    subject: primary ? `${subject} (ref ${primary})` : subject,
    reference: summary,
    message: `Support reference: ${summary}\n\n`,
  };
};
